import * as React from 'react';
import { DayPicker } from '@daypicker/react';
import { format } from 'date-fns';
import { Calendar } from 'lucide-react';
import { cn } from '../../utils';
import type { Locale } from 'date-fns';
import {
  DISPLAY_FORMAT,
  ISO_FORMAT,
  calendarClassNames,
  dateInputClassName,
  dateInputErrorClassName,
  dateInputSizeClassName,
  maskDateInput,
  parseInputDate,
  parseIsoDate,
  useCalendarPopoverStyle,
  useCloseOnOutsideClick,
} from './date-picker-shared';
import { CalendarPopover } from './calendar-popover';

export interface DatePickerProps {
  value?: string; // YYYY-MM-DD
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  id?: string;
  locale?: Locale;
  /** Keep the calendar inside a modal's focus trap and scrolling content. */
  inlineCalendar?: boolean;
  calendarLabel?: string;
  onInvalidInput?: (invalid: boolean) => void;
  'aria-describedby'?: string;
}

const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      placeholder,
      disabled,
      error,
      id,
      locale,
      inlineCalendar,
      calendarLabel = 'Open calendar',
      onInvalidInput,
      'aria-describedby': describedBy,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState('');
    const containerRef = React.useRef<HTMLDivElement>(null);
    const popoverRef = React.useRef<HTMLDivElement>(null);
    const calendarButtonRef = React.useRef<HTMLButtonElement>(null);
    const popoverStyle = useCalendarPopoverStyle(open, containerRef);

    const selected = React.useMemo(() => parseIsoDate(value), [value]);

    const displayValue = React.useMemo(() => {
      if (!selected) return '';
      return format(selected, DISPLAY_FORMAT);
    }, [selected]);

    React.useEffect(() => {
      setInputValue(displayValue);
    }, [displayValue]);

    useCloseOnOutsideClick(open, containerRef, popoverRef, setOpen);

    const commitInputValue = React.useCallback(() => {
      const trimmed = inputValue.trim();

      if (!trimmed) {
        onInvalidInput?.(false);
        onChange?.('');
        setInputValue('');
        return;
      }

      const parsedDate = parseInputDate(trimmed);
      if (!parsedDate) {
        onInvalidInput?.(true);
        if (!onInvalidInput) setInputValue(displayValue);
        return;
      }

      onInvalidInput?.(false);
      const nextValue = format(parsedDate, ISO_FORMAT);
      onChange?.(nextValue);
      setInputValue(format(parsedDate, DISPLAY_FORMAT));
    }, [displayValue, inputValue, onChange, onInvalidInput]);

    const handleSelect = (day: Date | undefined) => {
      if (!day || disabled) return;
      onInvalidInput?.(false);
      onChange?.(format(day, ISO_FORMAT));
      setInputValue(format(day, DISPLAY_FORMAT));
      setOpen(false);
      calendarButtonRef.current?.focus();
    };

    return (
      <div
        ref={containerRef}
        className="relative"
        onKeyDown={(event) => {
          if (open && event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
            calendarButtonRef.current?.focus();
          }
        }}
      >
        <input
          ref={ref}
          id={id}
          aria-invalid={error || undefined}
          aria-describedby={describedBy}
          type="text"
          disabled={disabled}
          inputMode="numeric"
          value={inputValue}
          placeholder={placeholder}
          onChange={(e) => setInputValue(maskDateInput(e.target.value))}
          onBlur={() => {
            commitInputValue();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitInputValue();
            }
          }}
          className={cn(
            dateInputClassName,
            dateInputSizeClassName.default,
            'pr-11',
            error && dateInputErrorClassName,
          )}
        />

        <button
          ref={calendarButtonRef}
          type="button"
          aria-label={calendarLabel}
          aria-expanded={open}
          aria-haspopup="dialog"
          disabled={disabled}
          onClick={() => setOpen((current) => !current)}
          className="absolute top-0 right-0 flex h-12 w-11 cursor-pointer items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Calendar className="size-4 shrink-0 opacity-70" />
        </button>

        <CalendarPopover
          open={open}
          popoverRef={popoverRef}
          style={popoverStyle}
          inline={inlineCalendar}
        >
          <DayPicker
            mode="single"
            showOutsideDays
            selected={selected}
            onSelect={handleSelect}
            defaultMonth={selected}
            locale={locale}
            disabled={disabled}
            classNames={calendarClassNames}
          />
        </CalendarPopover>
      </div>
    );
  },
);
DatePicker.displayName = 'DatePicker';

export { DatePicker };
